import { episodeDetailsActionTypes } from '@/admin/pages/studyGoEpisodeDetails/Constants';

import type { Episode, Hint, Room } from '@/admin/pages/studyGoBridgeEpisodes/types';

export interface GetEpisodeDetailsRequestAction {
	type: typeof episodeDetailsActionTypes.GET_EPISODE_DETAILS_REQUEST;
	payload: {
		episodeId: string;
	};
}

export interface GetEpisodeDetailsSuccessAction {
	type: typeof episodeDetailsActionTypes.GET_EPISODE_DETAILS_SUCCESS;
	payload: {
		episode: Episode;
	};
}

export interface GetEpisodeDetailsErrorAction {
	type: typeof episodeDetailsActionTypes.GET_EPISODE_DETAILS_ERROR;
	payload: {
		error: string;
	};
}

export interface CleanEpisodeDetailsStateAction {
	type: typeof episodeDetailsActionTypes.CLEAN_EPISODE_DETAILS_STATE;
}

export interface AddRoomRequestAction {
	type: typeof episodeDetailsActionTypes.ADD_ROOM_REQUEST;
	payload: {
		episodeId: string;
		roomData: {
			name: string;
			image_url: string;
		};
	};
}

export interface AddRoomSuccessAction {
	type: typeof episodeDetailsActionTypes.ADD_ROOM_SUCCESS;
	payload: {
		room: Room;
	};
}

export interface AddRoomErrorAction {
	type: typeof episodeDetailsActionTypes.ADD_ROOM_ERROR;
	payload: {
		error: string;
	};
}

export interface AddHintRequestAction {
	type: typeof episodeDetailsActionTypes.ADD_HINT_REQUEST;
	payload: {
		episodeId: string;
		hintData: {
			content: string;
		};
	};
}

export interface AddHintSuccessAction {
	type: typeof episodeDetailsActionTypes.ADD_HINT_SUCCESS;
	payload: {
		hint: Hint;
	};
}

export interface AddHintErrorAction {
	type: typeof episodeDetailsActionTypes.ADD_HINT_ERROR;
	payload: {
		error: string;
	};
}

export interface UpdateRoomRequestAction {
	type: typeof episodeDetailsActionTypes.UPDATE_ROOM_REQUEST;
	payload: {
		episodeId: string;
		roomId: string;
		roomData: {
			name?: string;
			image_url?: string;
		};
	};
}

export interface UpdateRoomSuccessAction {
	type: typeof episodeDetailsActionTypes.UPDATE_ROOM_SUCCESS;
	payload: {
		room: Room;
	};
}

export interface UpdateRoomErrorAction {
	type: typeof episodeDetailsActionTypes.UPDATE_ROOM_ERROR;
	payload: {
		error: string;
	};
}

export interface UpdateHintRequestAction {
	type: typeof episodeDetailsActionTypes.UPDATE_HINT_REQUEST;
	payload: {
		episodeId: string;
		hintId: string;
		hintData: {
			content?: string;
			position?: number;
		};
	};
}

export interface UpdateHintSuccessAction {
	type: typeof episodeDetailsActionTypes.UPDATE_HINT_SUCCESS;
	payload: {
		hint: Hint;
	};
}

export interface UpdateHintErrorAction {
	type: typeof episodeDetailsActionTypes.UPDATE_HINT_ERROR;
	payload: {
		error: string;
	};
}

export type EpisodeDetailsActions =
	| GetEpisodeDetailsRequestAction
	| GetEpisodeDetailsSuccessAction
	| GetEpisodeDetailsErrorAction
	| CleanEpisodeDetailsStateAction
	| AddRoomRequestAction
	| AddRoomSuccessAction
	| AddRoomErrorAction
	| AddHintRequestAction
	| AddHintSuccessAction
	| AddHintErrorAction
	| UpdateRoomRequestAction
	| UpdateRoomSuccessAction
	| UpdateRoomErrorAction
	| UpdateHintRequestAction
	| UpdateHintSuccessAction
	| UpdateHintErrorAction;

export function getEpisodeDetailsRequest(episodeId: string): GetEpisodeDetailsRequestAction {
	return {
		type: episodeDetailsActionTypes.GET_EPISODE_DETAILS_REQUEST,
		payload: { episodeId },
	};
}

export function getEpisodeDetailsSuccess(data: {
	episode: Episode;
}): GetEpisodeDetailsSuccessAction {
	return {
		type: episodeDetailsActionTypes.GET_EPISODE_DETAILS_SUCCESS,
		payload: data,
	};
}

export function getEpisodeDetailsError(error: string): GetEpisodeDetailsErrorAction {
	return {
		type: episodeDetailsActionTypes.GET_EPISODE_DETAILS_ERROR,
		payload: { error },
	};
}

export function cleanEpisodeDetailsState(): CleanEpisodeDetailsStateAction {
	return {
		type: episodeDetailsActionTypes.CLEAN_EPISODE_DETAILS_STATE,
	};
}

export function addRoomRequest(
	episodeId: string,
	roomData: { name: string; image_url: string },
): AddRoomRequestAction {
	return {
		type: episodeDetailsActionTypes.ADD_ROOM_REQUEST,
		payload: { episodeId, roomData },
	};
}

export function addRoomSuccess(data: { room: Room }): AddRoomSuccessAction {
	return {
		type: episodeDetailsActionTypes.ADD_ROOM_SUCCESS,
		payload: data,
	};
}

export function addRoomError(error: string): AddRoomErrorAction {
	return {
		type: episodeDetailsActionTypes.ADD_ROOM_ERROR,
		payload: { error },
	};
}

export function addHintRequest(
	episodeId: string,
	hintData: { content: string },
): AddHintRequestAction {
	return {
		type: episodeDetailsActionTypes.ADD_HINT_REQUEST,
		payload: { episodeId, hintData },
	};
}

export function addHintSuccess(data: { hint: Hint }): AddHintSuccessAction {
	return {
		type: episodeDetailsActionTypes.ADD_HINT_SUCCESS,
		payload: data,
	};
}

export function addHintError(error: string): AddHintErrorAction {
	return {
		type: episodeDetailsActionTypes.ADD_HINT_ERROR,
		payload: { error },
	};
}

export function updateRoomRequest(
	episodeId: string,
	roomId: string,
	roomData: { name?: string; image_url?: string },
): UpdateRoomRequestAction {
	return {
		type: episodeDetailsActionTypes.UPDATE_ROOM_REQUEST,
		payload: { episodeId, roomId, roomData },
	};
}

export function updateRoomSuccess(data: { room: Room }): UpdateRoomSuccessAction {
	return {
		type: episodeDetailsActionTypes.UPDATE_ROOM_SUCCESS,
		payload: data,
	};
}

export function updateRoomError(error: string): UpdateRoomErrorAction {
	return {
		type: episodeDetailsActionTypes.UPDATE_ROOM_ERROR,
		payload: { error },
	};
}

export function updateHintRequest(
	episodeId: string,
	hintId: string,
	hintData: { content?: string; position?: number },
): UpdateHintRequestAction {
	return {
		type: episodeDetailsActionTypes.UPDATE_HINT_REQUEST,
		payload: { episodeId, hintId, hintData },
	};
}

export function updateHintSuccess(data: { hint: Hint }): UpdateHintSuccessAction {
	return {
		type: episodeDetailsActionTypes.UPDATE_HINT_SUCCESS,
		payload: data,
	};
}

export function updateHintError(error: string): UpdateHintErrorAction {
	return {
		type: episodeDetailsActionTypes.UPDATE_HINT_ERROR,
		payload: { error },
	};
}
